import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { categories } from "@/constants/categories";
import { BUYER_FORM_URL, VENDOR_FORM_URL } from "@/constants/links";
import { MaskedLine, FadeIn } from "@/components/Reveal";

export default function CategoryPage() {
  const { slug } = useParams();
  const category = categories.find((c) => c.slug === slug);
  if (!category) return <Navigate to="/" replace />;
  const others = categories.filter((c) => c.slug !== slug);
  const Icon = category.icon;

  return (
    <>
      {/* Hero */}
      <section data-testid="category-page" className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24">
        <div className="pointer-events-none absolute -top-24 right-[-8%] h-[26rem] w-[26rem] rounded-full bg-[#1E3A8A]/10 blur-[110px]" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
          <div className="lg:col-span-6">
            <p className="eyebrow flex items-center gap-3" data-testid="category-eyebrow">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#1E3A8A]" />
              Launch Category · Planned
            </p>
            <h1 className="mt-8 font-sans text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>{category.name}</MaskedLine>
            </h1>
            <FadeIn delay={0.4}>
              <p className="mt-5 font-sans text-xl italic text-gold-gradient sm:text-2xl" data-testid="category-tagline">
                {category.tagline}
              </p>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
                {category.intro}
              </p>
            </FadeIn>
            <FadeIn delay={0.55} className="mt-10 flex flex-wrap gap-4">
              <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="category-cta-buyer" className="btn-gold">
                Join the Buyer Waitlist <ArrowUpRight size={16} />
              </a>
              {category.slug === "foods" ? (
                <Link to="/restaurant-signup" data-testid="category-cta-vendor" className="btn-ghost">
                  Restaurant / Food Business Onboarding
                </Link>
              ) : (
                <a href={VENDOR_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="category-cta-vendor" className="btn-ghost">
                  Sell in {category.name}
                </a>
              )}
            </FadeIn>
            {category.slug === "foods" && (
              <FadeIn delay={0.65}>
                <p className="mt-4 text-sm text-[#94A3B8]" data-testid="category-foods-general-note">
                  Not a restaurant?{" "}
                  <a href={VENDOR_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="category-cta-vendor-general" className="text-[#1E3A8A]/80 underline-offset-4 transition-colors hover:text-[#1E3A8A] hover:underline">
                    Use the general vendor form
                  </a>
                </p>
              </FadeIn>
            )}
          </div>
          <FadeIn delay={0.3} y={50} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-[#1E3A8A]/10 blur-3xl animate-pulse-glow" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-2xl border border-[#1E3A8A]/25 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
                <img
                  src={category.img}
                  alt={category.alt}
                  data-testid="category-hero-image"
                  className="aspect-[16/10] w-full object-cover grayscale-[30%] sepia-[15%] contrast-[1.05]"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#08080A]/70 via-transparent to-transparent" />
                <span className="absolute right-5 top-5 rounded-full border border-white/40 bg-white/90 px-4 py-1.5 font-sans text-[10px] tracking-[0.25em] text-[#1E3A8A] backdrop-blur-sm">
                  PLANNED FOR LAUNCH
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* What to expect */}
      <section data-testid="category-expect" className="border-t border-[#1E3A8A]/10 bg-[#F2F4F7]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-24 lg:px-16">
          <p className="eyebrow">At Launch</p>
          <h2 className="mt-6 font-sans text-3xl tracking-tight text-[#0F172A] sm:text-4xl">
            <MaskedLine view delay={0.1}>What to expect at launch.</MaskedLine>
          </h2>
          <div className="mt-12">
            {category.expect.map((item, i) => (
              <FadeIn key={item} delay={0.07 * i}>
                <div data-testid={`category-expect-${i + 1}`} className="group grid grid-cols-1 gap-3 border-t border-[#E2E8F0] py-7 transition-colors duration-500 hover:border-[#1E3A8A]/30 sm:grid-cols-12 sm:items-baseline sm:gap-8">
                  <span className="font-sans text-sm tracking-[0.25em] text-[#1E3A8A] sm:col-span-2">0{i + 1}</span>
                  <p className="font-sans text-lg text-[#0F172A] transition-colors duration-500 group-hover:text-[#1D4ED8] sm:col-span-10 sm:text-xl">{item}</p>
                </div>
              </FadeIn>
            ))}
            <div className="border-t border-[#E2E8F0]" />
          </div>
        </div>
      </section>

      {/* Shelf placeholder — structure for future products */}
      <section data-testid="category-shelf" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-24 lg:px-16">
        <FadeIn>
          <div data-testid="category-shelf-placeholder" className="flex flex-col items-center rounded-2xl border border-dashed border-[#1E3A8A]/30 bg-[#F2F4F7] px-6 py-20 text-center">
            <span className="flex h-14 w-14 rotate-45 items-center justify-center rounded-xl border border-[#1E3A8A]/40">
              <Icon size={24} className="-rotate-45 text-[#1E3A8A]" />
            </span>
            <h2 className="mt-8 font-sans text-2xl text-[#0F172A] sm:text-3xl">The shelves are being set.</h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-[#64748B] sm:text-base">
              Real products and sellers will appear here as Aflimart approaches launch.
              Nothing on this page is for sale yet — join the waitlist to be first in.
            </p>
            <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="category-shelf-cta" className="btn-gold mt-8 !px-6 !py-3 !text-xs">
              Be First to Shop {category.name} <ArrowUpRight size={14} />
            </a>
          </div>
        </FadeIn>
      </section>

      {/* Other categories */}
      <section data-testid="category-explore" className="border-t border-[#1E3A8A]/10 bg-[#F2F4F7]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
          <p className="eyebrow">Keep Exploring</p>
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/categories/${c.slug}`}
                data-testid={`category-link-${c.slug}`}
                className="group flex items-center justify-between border-b border-[#E2E8F0] py-5 transition-colors duration-300 hover:border-[#1E3A8A]/40"
              >
                <span className="font-sans text-lg text-[#475569] transition-colors duration-300 group-hover:text-[#1D4ED8] sm:text-xl">{c.name}</span>
                <ArrowRight size={18} className="text-[#1E3A8A]/50 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#1E3A8A]" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
